import { useEffect, useMemo, useRef, useState } from "react";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {
	Alert,
	Box,
	CircularProgress,
	Paper,
	Snackbar,
	Typography,
} from "@mui/material";
import type {
	ItemRoll,
	PurchaseOrder,
	PurchaseOrderItem,
} from "../models/RollsReceived";
import {
	useIsRollsReceivedImportMode,
	useItemRolls,
	usePurchaseOrderDetails,
	usePurchaseOrders,
	useRemoveItemRoll,
	useUpsertItemRoll,
} from "../hooks/useRollsReceived";
import { ItemRollsTable } from "./ItemRollsTable";
import { PurchaseOrderItemsTable } from "./PurchaseOrderItemsTable";
import { RollDetailViewer } from "./RollDetailViewer";
import { RollsReceivedToolbar } from "./RollsReceivedToolbar";
import { DeleteConfirmModal } from "@/controls/modals/DeleteConfirmModal";
import {
	getLastSelectedPurchaseOrderId,
	setLastSelectedPurchaseOrderId,
} from "@/shared/utils/lastSelectedPurchaseOrder";
import {
	OPERATIONAL_WRITES_LOCKED_MESSAGE,
	useOperationalWritesLocked,
} from "@/shared/hooks/useOperationalWritesLocked";
import { urlParam, useUrlState } from "@/shared/hooks/useUrlState";

const URL_SCHEMA = {
	purchaseOrderId: urlParam.int(),
	itemId: urlParam.int(),
	// The roll slot, which is a position inside the item rather than an id.
	slot: urlParam.int(),
};

interface RollForm {
	Id: number | null;
	Name: string;
	Hardness: number | null;
	SupplierInvoiceNumber: string;
}

// Numbered card title + connector arrow: the page reads as a 3-step flow
// (pick the item -> pick the slot -> edit the roll).
const StepTitle = ({ n, label }: { n: number; label: string }) => (
	<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
		<Box
			sx={{
				width: 22,
				height: 22,
				borderRadius: "50%",
				bgcolor: "primary.main",
				color: "primary.contrastText",
				fontSize: 13,
				fontWeight: 700,
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				flexShrink: 0,
			}}
		>
			{n}
		</Box>
		<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
			{label}
		</Typography>
	</Box>
);

const StepArrow = () => (
	<Box
		sx={{
			display: "flex",
			alignItems: "center",
			color: "text.disabled",
			flexShrink: 0,
		}}
	>
		<ArrowForwardIcon fontSize="small" />
	</Box>
);

export const RollsReceivedManager = () => {
	// Received rolls are RollShop-owned: read-only in Import mode (SP enforces
	// it too). Read the setting FRESH (own query, refetch on mount/focus) so an
	// admin flipping ImportRolls blocks writes on return without a full reload.
	const importMode = useIsRollsReceivedImportMode();
	const writesLocked = useOperationalWritesLocked();
	const isNewMode = !importMode && !writesLocked;

	const [
		{ purchaseOrderId, itemId, slot: selectedOrder },
		setFilters,
		{ dropValues },
	] = useUrlState(URL_SCHEMA);
	const [rollToDelete, setRollToDelete] = useState<ItemRoll | null>(null);
	const [error, setError] = useState<string | null>(null);

	// Legacy parity: the PO typeahead (CAT.FindPurchaseOrders) offered active
	// purchase orders only.
	const posQuery = usePurchaseOrders(null, true);
	// The three steps of the flow travel as ids and each one is resolved against what the
	// step above returned, so a link reopens the same order, item and slot.
	const selectedPO = useMemo(
		() =>
			purchaseOrderId === null
				? null
				: ((posQuery.data ?? []).find((po) => po.Id === purchaseOrderId) ??
					null),
		[posQuery.data, purchaseOrderId],
	);
	const itemsQuery = usePurchaseOrderDetails(selectedPO?.Id ?? null);
	const selectedItem = useMemo(
		() =>
			itemId === null
				? null
				: ((itemsQuery.data ?? []).find((item) => item.Id === itemId) ?? null),
		[itemsQuery.data, itemId],
	);
	const rollsQuery = useItemRolls(selectedItem?.Id ?? null);

	const upsertMutation = useUpsertItemRoll();
	const removeMutation = useRemoveItemRoll();
	const isSaving = upsertMutation.isPending || removeMutation.isPending;

	const rolls = rollsQuery.data ?? [];
	const selectedRoll = useMemo(
		() => rolls.find((r) => r.Order === selectedOrder) ?? null,
		[rolls, selectedOrder],
	);

	// An order, item or slot that the step above no longer offers would leave the next panel
	// empty, so the key goes.
	useEffect(() => {
		if (!posQuery.isSuccess || purchaseOrderId === null) return;
		if (!(posQuery.data ?? []).some((po) => po.Id === purchaseOrderId))
			dropValues("purchaseOrderId", "itemId", "slot");
	}, [posQuery.isSuccess, posQuery.data, purchaseOrderId, dropValues]);

	useEffect(() => {
		if (!itemsQuery.isSuccess || itemId === null) return;
		if (!(itemsQuery.data ?? []).some((item) => item.Id === itemId))
			dropValues("itemId", "slot");
	}, [itemsQuery.isSuccess, itemsQuery.data, itemId, dropValues]);

	// An emptied slot survives: the procedure pads the list up to QuantityOrdered, so the
	// slot still exists after its roll is removed and the editor reopens blank.
	useEffect(() => {
		if (!rollsQuery.isSuccess || selectedOrder === null) return;
		if (!(rollsQuery.data ?? []).some((roll) => roll.Order === selectedOrder))
			dropValues("slot");
	}, [rollsQuery.isSuccess, rollsQuery.data, selectedOrder, dropValues]);

	// UX: default the PO selector to the one the operator last worked with
	// (localStorage, shared with the Purchase Orders screen). Falls back to the
	// most recent PO by DateOrdered when nothing is stored. Runs once, only while
	// nothing is selected yet.
	const didAutoSelectPO = useRef(false);
	useEffect(() => {
		if (didAutoSelectPO.current) return;
		// A link decides: what it carries must not be overridden by the stored order.
		if (purchaseOrderId !== null) {
			didAutoSelectPO.current = true;
			return;
		}
		const pos = posQuery.data;
		if (!pos || pos.length === 0) return;
		didAutoSelectPO.current = true;

		const storedId = getLastSelectedPurchaseOrderId();
		const stored =
			storedId != null ? pos.find((p) => p.Id === storedId) : undefined;

		const orderedAt = (s?: string | null) => {
			if (!s) return Number.NEGATIVE_INFINITY;
			const t = new Date(s).getTime();
			return Number.isNaN(t) ? Number.NEGATIVE_INFINITY : t;
		};
		const mostRecent = pos.reduce((a, b) =>
			orderedAt(b.DateOrdered) > orderedAt(a.DateOrdered) ? b : a,
		);

		const target = stored ?? mostRecent;
		setFilters({ purchaseOrderId: target.Id, itemId: null, slot: null });
		setLastSelectedPurchaseOrderId(target.Id);
	}, [posQuery.data, purchaseOrderId, setFilters]);

	const handleSelectPO = (po: PurchaseOrder | null) => {
		setFilters({ purchaseOrderId: po?.Id ?? null, itemId: null, slot: null });
		// Remember the operator's choice for next time / the other screen.
		if (po) setLastSelectedPurchaseOrderId(po.Id);
	};

	const handleSelectItem = (item: PurchaseOrderItem) => {
		setFilters({ itemId: item.Id, slot: null });
	};

	const handleSave = (form: RollForm) => {
		if (!selectedItem) return;
		upsertMutation.mutate(
			{
				Id: form.Id ?? null,
				Name: form.Name.trim(),
				PurchaseOrderItemId: selectedItem.Id,
				Hardness: form.Hardness as number,
				SupplierInvoiceNumber: form.SupplierInvoiceNumber || null,
			},
			{ onError: (e: unknown) => setError((e as Error).message) },
		);
	};

	const handleConfirmDelete = () => {
		if (rollToDelete?.Id == null) return;
		// Keep the slot selected: after the roll is removed it becomes an empty
		// slot, so the editor reopens blank and ready to record a new roll.
		removeMutation.mutate(rollToDelete.Id, {
			onSuccess: () => setRollToDelete(null),
			onError: (e: unknown) => setError((e as Error).message),
		});
	};

	if (posQuery.isLoading) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
				<CircularProgress />
			</Box>
		);
	}

	return (
		<Box
			sx={{
				p: 2,
				height: "calc(100vh - 64px)",
				display: "flex",
				flexDirection: "column",
			}}
		>
			<Typography variant="h5" sx={{ fontWeight: 600, mb: 1.5 }}>
				Rolls Received
			</Typography>

			<RollsReceivedToolbar
				purchaseOrders={posQuery.data ?? []}
				selectedPO={selectedPO}
				loading={posQuery.isFetching}
				onSelect={handleSelectPO}
				onRefresh={() => posQuery.refetch()}
			/>

			{/* Three-step flow, left to right: pick the order item, pick the roll
			    slot, edit the roll. The arrows + step badges make the selection
			    chain explicit. */}
			<Box
				sx={{
					display: "flex",
					gap: 1,
					alignItems: "stretch",
					flex: 1,
					minHeight: 480,
				}}
			>
				<Paper
					elevation={3}
					sx={{
						p: 1.5,
						flex: "1.25 1 0",
						minWidth: 380,
						display: "flex",
						flexDirection: "column",
					}}
				>
					<StepTitle
						n={1}
						label={
							selectedPO
								? `Purchase Order: ${selectedPO.Number?.trim()}`
								: "Purchase Order Items"
						}
					/>
					<Box sx={{ flex: 1, minHeight: 0, width: "100%" }}>
						<PurchaseOrderItemsTable
							items={itemsQuery.data ?? []}
							selectedId={selectedItem?.Id ?? null}
							onSelect={handleSelectItem}
						/>
					</Box>
				</Paper>

				<StepArrow />

				<Paper
					elevation={3}
					sx={{
						p: 1.5,
						flex: "1 1 0",
						minWidth: 300,
						display: "flex",
						flexDirection: "column",
					}}
				>
					<StepTitle
						n={2}
						label={
							selectedItem
								? `Item ${selectedItem.ItemNumber} Rolls`
								: "Item Rolls"
						}
					/>
					{selectedItem ? (
						<Box sx={{ flex: 1, minHeight: 0, width: "100%" }}>
							<ItemRollsTable
								rolls={rolls}
								selectedOrder={selectedOrder}
								onSelect={(roll) => setFilters({ slot: roll.Order })}
								onDelete={(roll) => setRollToDelete(roll)}
								writeDisabled={!isNewMode}
								writeDisabledReason={
									writesLocked
										? OPERATIONAL_WRITES_LOCKED_MESSAGE
										: "Read-only."
								}
							/>
						</Box>
					) : (
						<Box
							sx={{
								display: "flex",
								flex: 1,
								justifyContent: "center",
								alignItems: "center",
								color: "text.secondary",
							}}
						>
							<Typography>Select an item on the left</Typography>
						</Box>
					)}
				</Paper>

				<StepArrow />

				<Paper
					elevation={3}
					sx={{
						p: 1.5,
						flex: "1.1 1 0",
						minWidth: 340,
						display: "flex",
						flexDirection: "column",
					}}
				>
					<StepTitle n={3} label="Received Roll" />
					<Box sx={{ flex: 1, minHeight: 0 }}>
						<RollDetailViewer
							item={selectedItem}
							roll={selectedRoll}
							isNewMode={isNewMode}
							writesLocked={writesLocked}
							isSaving={isSaving}
							onSave={handleSave}
						/>
					</Box>
				</Paper>
			</Box>

			<DeleteConfirmModal
				open={!!rollToDelete}
				title="REMOVE ROLL"
				itemName={
					rollToDelete?.Name
						? rollToDelete.Name
						: `slot ${rollToDelete?.Order ?? ""}`
				}
				isDeleting={removeMutation.isPending}
				onConfirm={handleConfirmDelete}
				onCancel={() => setRollToDelete(null)}
			/>

			<Snackbar
				open={!!error}
				autoHideDuration={6000}
				onClose={() => setError(null)}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert severity="error" onClose={() => setError(null)}>
					{error}
				</Alert>
			</Snackbar>
		</Box>
	);
};
